import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import api, { errMsg } from "../../lib/api";

const fieldCls = "w-full rounded-md border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-saffron-500/50";
const ASPECTS = [["16:9", "Landscape 16:9"], ["banner", "Website Banner"], ["1:1", "Square 1:1"], ["4:5", "Portrait 4:5"]];

export default function AIImageStudio({ open, onClose, onUse, entity = "gallery", initialPrompt = "", saveLabel = "Use Image" }) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [aspect, setAspect] = useState("16:9");
  const [busy, setBusy] = useState(false);
  const [metaBusy, setMetaBusy] = useState(false);
  const [image, setImage] = useState("");
  const [meta, setMeta] = useState({ title: "", heading: "", caption: "", alt_text: "", category: "", description: "", tags_csv: "", seo_title: "", seo_desc: "" });

  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || "");
      setImage("");
      setMeta({ title: "", heading: "", caption: "", alt_text: "", category: "", description: "", tags_csv: "", seo_title: "", seo_desc: "" });
    }
  }, [open, initialPrompt]);

  if (!open) return null;

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe the image you want AI to create");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/ai/cms/image/preview", { prompt, aspect });
      setImage(r.data.image);
      toast.success("AI image preview generated — not saved yet");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const suggestMeta = async () => {
    setMetaBusy(true);
    try {
      const r = await api.post("/ai/cms/image-metadata", { prompt, entity });
      const data = r.data || {};
      setMeta({ ...meta, ...data, tags_csv: Array.isArray(data.tags) ? data.tags.join(", ") : data.tags_csv || "" });
      toast.success("AI metadata suggested — edit before saving");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMetaBusy(false);
    }
  };

  const save = async () => {
    if (!image) return;
    setBusy(true);
    try {
      let finalMeta = meta;
      if (!meta.title || !meta.alt_text) {
        const m = await api.post("/ai/cms/image-metadata", { prompt, entity });
        finalMeta = { ...meta, ...m.data, tags_csv: Array.isArray(m.data.tags) ? m.data.tags.join(", ") : meta.tags_csv };
        setMeta(finalMeta);
      }
      const tags = finalMeta.tags_csv.split(",").map((t) => t.trim()).filter(Boolean);
      const r = await api.post("/ai/cms/image/save", { image_base64: image, prompt, aspect, ...finalMeta, tags });
      toast.success("AI image saved to Media Library");
      onUse?.(r.data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-start justify-center overflow-y-auto bg-navy/65 p-4 backdrop-blur-sm" data-testid="ai-image-modal">
      <div className="my-8 w-full max-w-4xl rounded-xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold text-navy"><Sparkles size={18} className="mr-2 inline text-saffron-600" />Generate Image with AI</h2>
            <p className="mt-1 text-xs text-slate-500">Generate → Preview → Accept → Media Library → Content. Nothing is public automatically.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-gray-100" data-testid="ai-image-cancel" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Tell AI what image you want</label>
            <textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="Create a respectful NGO awareness illustration representing inclusive education, healthcare and social security in India. Do not use identifiable real persons."
              className={`${fieldCls} mt-2`} data-testid="ai-image-prompt" />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <select value={aspect} onChange={(e) => setAspect(e.target.value)} className={`${fieldCls} !w-48`} data-testid="ai-image-aspect">
                {ASPECTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <button onClick={generate} disabled={busy} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="ai-image-generate">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {image ? "Regenerate" : "Generate"}
              </button>
              {image && <button onClick={() => setImage("")} className="rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-navy" data-testid="ai-image-change-prompt">Edit Prompt</button>}
            </div>
            {busy && <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-saffron-600" data-testid="ai-image-loading"><Loader2 size={15} className="animate-spin" /> Generating…</p>}
            <div className="mt-5 flex min-h-72 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-navy-50/40 p-4">
              {image ? <img src={image} alt={meta.alt_text || "AI generated preview"} className="max-h-[420px] rounded-lg object-contain" data-testid="ai-image-preview" />
                : <p className="text-sm text-slate-400">AI image preview will appear here</p>}
            </div>
          </div>

          <div className="space-y-3">
            <button onClick={suggestMeta} disabled={!image || metaBusy} className="btn-dark w-full !rounded-md !px-4 !py-2.5 text-sm disabled:opacity-50" data-testid="ai-image-suggest-meta">
              {metaBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} AI Caption & Metadata
            </button>
            {[
              ["title", "Media Title"], ["heading", "Heading"], ["caption", "Caption"], ["alt_text", "Alt Text"],
              ["category", "Category"], ["tags_csv", "Tags (comma separated)"], ["seo_title", "SEO Title"], ["seo_desc", "SEO Description"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
                {key === "caption" || key === "alt_text" || key === "seo_desc" ? (
                  <textarea rows={key === "seo_desc" ? 3 : 2} value={meta[key]} onChange={(e) => setMeta({ ...meta, [key]: e.target.value })} className={fieldCls} data-testid={`ai-meta-${key}`} />
                ) : (
                  <input value={meta[key]} onChange={(e) => setMeta({ ...meta, [key]: e.target.value })} className={fieldCls} data-testid={`ai-meta-${key}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-7 flex flex-wrap justify-end gap-3">
          {image && <a href={image} download={`ai-${aspect.replace(":", "x")}.webp`} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid="ai-image-download"><Download size={14} /> Download</a>}
          <button onClick={onClose} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid="ai-image-cancel-bottom">Cancel</button>
          <button onClick={save} disabled={!image || busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="ai-image-use">
            {busy && <Loader2 size={14} className="animate-spin" />} {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
